// App.js
import { useEffect, useMemo, useState, useContext } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import { LanguageProvider } from "./context/LanguageContext";
import useAppRoutes from "routes/useAppRoutes";
import { useMaterialUIController, setMiniSidenav } from "context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { AuthProvider, useAuth } from "./AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { LanguageContext } from "./context/LanguageContext";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { saveReferralCodeFromUrl } from "./utils/referral";
import { setUnauthorizedHandler } from "layouts/mas-genie/utils/genieApi";
import { trackPageView } from "./utils/analytics";
import CrispChat from "components/CrispChat";

const clientId = "148195292685-h9llvptiqj9bdhvuh64ang6j18atdnbg.apps.googleusercontent.com";

// 🧭 頁面瀏覽 beacon：登入後每次換頁寫一筆到後端（fire-and-forget；用原生 fetch 避開 axios 401 攔截導頁）
let __lastPageviewPath = null;
function logPageViewBeacon(path) {
  try {
    if (!path || path === __lastPageviewPath) return;
    if (path.startsWith("/authentication")) return; // 登入/註冊等未登入頁不記
    const token = localStorage.getItem("access_token");
    if (!token) return; // 未登入不記
    __lastPageviewPath = path;
    fetch("/api/users/pageview", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ path, title: typeof document !== "undefined" ? document.title : "" }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* no-op：頁面軌跡不影響前端 */
  }
}

export default function App() {


  return (
    <GoogleOAuthProvider clientId={clientId}>

      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

function AppContent() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    saveReferralCodeFromUrl();
    loadLanguageFile("App", "app/App_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = componentLanguageData.App || { Main: {} };
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { isAuthenticated, isInitializing, user } = useAuth();
  const navigate = useNavigate();

  const routes = useAppRoutes(isAuthenticated && !isInitializing);
  const sidenavRoutes = useMemo(() => routes, [routes]);
  const { logout } = useAuth();

  // 全域 401 攔截（MAS 精靈 API client 用）— 註冊一次，所有頁面 fetch 都吃這個
  // 避免單頁註冊在路由切換時互相覆蓋造成最終 handler=null
  useEffect(() => {
    setUnauthorizedHandler(() => {
      try { logout?.(); } catch (_) { }
      navigate('/authentication/sign-in', { replace: true });
    });
  }, [logout, navigate]);

  // console.log("AuthContext →", {
  //   token,
  //   isAuthenticated,
  //   isInitializing,
  //   pathname,
  // });

  useEffect(() => {
    const cache = createCache({ key: "rtl", stylisPlugins: [rtlPlugin] });
    setRtlCache(cache);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // GA4：SPA 切頁送 page_view（首次 mount 也會送一次）
  useEffect(() => {
    trackPageView(pathname);
    logPageViewBeacon(pathname); // 🧭 登入後頁面軌跡（供後台使用追蹤）
  }, [pathname]);

  // ✅ 自動登出邏輯（閒置 15 分鐘）
  useEffect(() => {
    if (!isAuthenticated) return;

    // 如果是管理員，不執行自動登出邏輯
    if (user?.role === "ADMIN") return;

    const IDLE_LIMIT = 30 * 60 * 1000; // 15 分鐘（非 0.5 分鐘）
    let logoutTimer = null;
    let hasLoggedOut = false;

    const handleLogout = () => {
      if (hasLoggedOut) return;
      hasLoggedOut = true;

      toast.info(LANGUAGE.Main.Content2);

      logout();

      setTimeout(() => {
        navigate("/authentication/sign-in", { replace: true });
      }, 1000); // 1 秒後導回登入頁
    };

    const resetIdleTimer = () => {
      clearTimeout(logoutTimer);
      logoutTimer = setTimeout(handleLogout, IDLE_LIMIT);
    };

    // 綁定常見使用者互動事件
    const activityEvents = ["mousemove", "keydown", "scroll", "click"];
    activityEvents.forEach((event) =>
      window.addEventListener(event, resetIdleTimer)
    );

    resetIdleTimer(); // 初始化計時器

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetIdleTimer)
      );
      clearTimeout(logoutTimer);
    };
  }, [isAuthenticated, navigate, logout, LANGUAGE.Main.Content2]);

  if (isInitializing) return null;

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const LayoutComponent = (
    <>
      {/* 2026-05-25 移除 marginLeft:10 包裝 div：讓 sidebar 與 top bar 連在一起無縫接 */}
      <Sidenav
        routes={sidenavRoutes}
        color={sidenavColor}
        brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
        brandName={LANGUAGE.Main.Content1}
      />
      <Configurator />
    </>
  );

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.route) {
        return [<Route exact path={route.route} element={route.component} key={route.route} />];
      }
      if (route.children) {
        return getRoutes(route.children);
      }
      return [];
    });

  const AllRoutes = (
    <Routes>
      {getRoutes(routes)}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <CrispChat />
        <ToastContainer position="top-center" autoClose={3000} />
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          {layout === "dashboard" && LayoutComponent}
          <div style={{ flex: 1, backgroundColor: layout === "page" ? "#E6E6E6" : undefined }}>{AllRoutes}</div>
          {/* 2026-05-25 footer 補 sidebar margin（RTL 用 right），避免左/右側公司字被固定 sidebar 蓋住 */}
          <MDBox
            sx={(theme) => ({
              ...(layout === "dashboard" && {
                [theme.breakpoints.up("xl")]: {
                  marginRight: miniSidenav ? "96px" : "250px",
                },
              }),
            })}
          >
            <Footer />
          </MDBox>
        </div>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <CrispChat />
      <ToastContainer position="top-center" autoClose={3000} />
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {layout === "dashboard" && LayoutComponent}
        <div style={{ flex: 1, backgroundColor: layout === "page" ? "#E6E6E6" : undefined }}>{AllRoutes}</div>
        {/* 2026-05-25 footer 補 sidebar marginLeft，避免左側公司字被固定 sidebar 蓋住 */}
        <MDBox
          sx={(theme) => ({
            ...(layout === "dashboard" && {
              [theme.breakpoints.up("xl")]: {
                marginLeft: miniSidenav ? "96px" : "250px",
                transition: theme.transitions.create("margin-left", {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.standard,
                }),
              },
            }),
          })}
        >
          <Footer />
        </MDBox>
      </div>
    </ThemeProvider>
  );
}
